/* ============================================================
   Vivonu — Theme interactions
   Progressive enhancement: everything degrades gracefully.
   ============================================================ */
(function () {
  'use strict';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveals(root) {
    var els = Array.prototype.slice.call((root || document).querySelectorAll('.fp-reveal:not(.is-in)'));
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    // subtle stagger for siblings that reveal together
    els.forEach(function (el) {
      var i = 0, sib = el;
      while ((sib = sib.previousElementSibling)) { if (sib.classList && sib.classList.contains('fp-reveal')) i++; }
      if (i > 0) el.style.transitionDelay = Math.min(i * 60, 240) + 'ms';
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Hero auto slider ---------- */
  function initHeroSliders(root) {
    (root || document).querySelectorAll('[data-fp-hero-slider]').forEach(function (slider) {
      var slides = Array.prototype.slice.call(slider.querySelectorAll('.fp-slide'));
      var dots = Array.prototype.slice.call(slider.querySelectorAll('.fp-hero__dot'));
      if (slides.length < 2) return;
      var i = 0, timer = null;
      var interval = parseInt(slider.getAttribute('data-interval'), 10) || 5000;
      function go(n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
        dots.forEach(function (d, k) {
          d.classList.toggle('is-active', k === i);
          d.setAttribute('aria-selected', k === i ? 'true' : 'false');
        });
      }
      function start() { if (!reduceMotion) { stop(); timer = setInterval(function () { go(i + 1); }, interval); } }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k); start(); }); });
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });
      go(0); start();
    });
  }

  /* ---------- Size guide <-> product selector sync ---------- */
  function initSizeGuide(root) {
    (root || document).querySelectorAll('[data-fp-size-guide]').forEach(function (guide) {
      var cards = Array.prototype.slice.call(guide.querySelectorAll('.fp-size-card'));
      var chips = Array.prototype.slice.call(guide.querySelectorAll('.fp-size-chip'));
      function select(size) {
        cards.forEach(function (c) {
          var on = c.getAttribute('data-size') === size;
          c.classList.toggle('is-selected', on);
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        chips.forEach(function (ch) {
          var on = ch.getAttribute('data-size') === size;
          ch.classList.toggle('is-selected', on);
          ch.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      cards.forEach(function (c) {
        c.addEventListener('click', function () { select(c.getAttribute('data-size')); });
        c.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(c.getAttribute('data-size')); }
        });
      });
      chips.forEach(function (ch) {
        ch.addEventListener('click', function () {
          var size = ch.getAttribute('data-size');
          select(size);
          var card = guide.querySelector('.fp-size-card[data-size="' + size + '"]');
          if (card && card.scrollIntoView) card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
        });
      });
    });
  }

  /* ---------- Color variants ---------- */
  function initColorVariants(root) {
    (root || document).querySelectorAll('[data-fp-colors]').forEach(function (block) {
      var swatches = Array.prototype.slice.call(block.querySelectorAll('.fp-swatch'));
      var layers = Array.prototype.slice.call(block.querySelectorAll('.fp-colors__stage .fp-media'));
      function select(idx) {
        swatches.forEach(function (s, k) {
          s.classList.toggle('is-selected', k === idx);
          s.setAttribute('aria-pressed', k === idx ? 'true' : 'false');
        });
        layers.forEach(function (l, k) { l.classList.toggle('is-active', k === idx); });
      }
      swatches.forEach(function (s, k) { s.addEventListener('click', function () { select(k); }); });
      if (swatches.length) select(0);
    });
  }

  /* ---------- Before / After slider ---------- */
  function initBeforeAfter(root) {
    (root || document).querySelectorAll('[data-fp-before-after]').forEach(function (ba) {
      var range = ba.querySelector('.fp-ba__range');
      if (!range) return;
      function set(v) { ba.style.setProperty('--fp-ba-pos', v + '%'); }
      range.addEventListener('input', function () { set(range.value); });
      set(range.value || 50);
    });
  }

  /* ---------- Mobile menu ---------- */
  function initMenu(root) {
    (root || document).querySelectorAll('[data-fp-menu-toggle], [data-vh-menu-toggle]').forEach(function (btn) {
      if (btn.getAttribute('data-vh-menu-bound') === 'true') return;

      var target = document.getElementById(btn.getAttribute('aria-controls'));
      if (!target) return;

      btn.setAttribute('data-vh-menu-bound', 'true');

      function setOpen(open) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        target.hidden = !open;
        document.documentElement.classList.toggle('vivonu-mobile-menu-open', open);
      }

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        setOpen(!open);
      });

      target.querySelectorAll('a').forEach(function (link) {
        link.addEventListener('click', function () { setOpen(false); });
      });
      target.querySelectorAll('[data-vh-menu-close]').forEach(function(closeBtn){
        closeBtn.addEventListener('click', function(){ setOpen(false); btn.focus(); });
      });

      btn.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          setOpen(false);
          btn.focus();
        }
      });
    });
  }

  /* ---------- Sticky header scroll state ---------- */
  function initHeaderScroll() {
    var header = document.querySelector('.fp-header');
    if (!header) return;
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        header.classList.toggle('is-scrolled', window.scrollY > 8);
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }


  /* ---------- Vivonu delivery location ---------- */
  function initDeliveryLocation(root) {
    var scope = root || document;
    var triggers = scope.querySelectorAll('.vivonu-delivery-location-trigger');
    var dialog = document.getElementById('VivonuDeliveryLocationDialog');
    if (!dialog || !triggers.length || dialog.getAttribute('data-vdl-bound') === 'true') return;
    dialog.setAttribute('data-vdl-bound','true');
    var panel = dialog.querySelector('.vivonu-delivery-location__panel');
    var form = dialog.querySelector('[data-vdl-form]');
    var status = dialog.querySelector('[data-vdl-status]');
    var geo = dialog.querySelector('[data-vdl-geo]');
    var country = form && form.elements.country;
    var area = form && form.elements.area;
    var postal = form && form.elements.postal;
    var key = 'vivonu_delivery_location_v1';
    var lastFocus = null;

    function readSaved(){
      try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch(e){ return null; }
    }
    function applySaved(data){
      if (!data) return;
      var countryLabel = data.country || 'South Africa';
      var label = [data.area, data.postal].filter(Boolean).join(' · ') || countryLabel || data.label || 'South Africa';
      document.querySelectorAll('[data-vdl-header-label]').forEach(function(el){ el.textContent = label; });
      document.querySelectorAll('[data-vdl-mobile-label]').forEach(function(el){ el.textContent = label; });
      document.querySelectorAll('[data-vdl-drawer-label]').forEach(function(el){ el.textContent = label; });
      document.querySelectorAll('.vivonu-delivery-location-trigger').forEach(function(el){
        el.setAttribute('aria-label','Deliver to ' + label + (label === countryLabel ? '' : ', ' + countryLabel));
        el.setAttribute('title','Deliver to ' + label + (label === countryLabel ? '' : ', ' + countryLabel));
      });
    }
    function openDialog(trigger){
      lastFocus = trigger || document.activeElement;
      var saved = readSaved();
      if (saved && country) country.value = saved.country || country.value;
      if (saved && area) area.value = saved.area || '';
      if (saved && postal) postal.value = saved.postal || '';
      if (status) status.textContent = '';
      dialog.hidden = false;
      document.documentElement.classList.add('vivonu-location-open');
      requestAnimationFrame(function(){ (area || panel).focus && (area || panel).focus(); });
    }
    function closeDialog(){
      dialog.hidden = true;
      document.documentElement.classList.remove('vivonu-location-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    document.querySelectorAll('.vivonu-delivery-location-trigger').forEach(function(btn){
      btn.addEventListener('click', function(){ openDialog(btn); });
    });
    dialog.querySelectorAll('[data-vdl-close]').forEach(function(btn){ btn.addEventListener('click', closeDialog); });
    dialog.addEventListener('keydown', function(e){ if(e.key === 'Escape') closeDialog(); });

    if (geo) geo.addEventListener('click', function(){
      if (!navigator.geolocation) { if(status) status.textContent='Location services are not available in this browser.'; return; }
      geo.disabled = true;
      if(status) status.textContent='Requesting your location…';
      navigator.geolocation.getCurrentPosition(function(pos){
        var data = readSaved() || {};
        data.lat = Number(pos.coords.latitude.toFixed(5));
        data.lng = Number(pos.coords.longitude.toFixed(5));
        data.label = 'Current location';
        data.country = country && country.value ? country.value : 'South Africa';
        data.area = area && area.value ? area.value.trim() : '';
        data.postal = postal && postal.value ? postal.value.trim() : '';
        data.source = 'geolocation';
        localStorage.setItem(key, JSON.stringify(data));
        applySaved(data);
        if(status) status.textContent='Current location selected. You can also add your suburb or postal code.';
        geo.disabled = false;
      }, function(){
        if(status) status.textContent='We could not access your location. Enter your suburb or city instead.';
        geo.disabled = false;
      }, { enableHighAccuracy:false, timeout:10000, maximumAge:300000 });
    });

    if (form) form.addEventListener('submit', function(e){
      e.preventDefault();
      var data = readSaved() || {};
      data.country = country && country.value ? country.value : 'South Africa';
      data.area = area ? area.value.trim() : '';
      data.postal = postal ? postal.value.trim() : '';
      data.label = [data.area, data.postal].filter(Boolean).join(' · ') || data.country;
      data.source = data.source || 'manual';
      localStorage.setItem(key, JSON.stringify(data));
      applySaved(data);
      if(status) status.textContent='Delivery location saved.';
      setTimeout(closeDialog, 450);
    });

    applySaved(readSaved());
  }

  function initAll(root) {
    initReveals(root);
    initHeroSliders(root);
    initSizeGuide(root);
    initColorVariants(root);
    initBeforeAfter(root);
    initMenu(root);
    initDeliveryLocation(root);
  }

  if (document.readyState !== 'loading') { initAll(document); initHeaderScroll(); }
  else document.addEventListener('DOMContentLoaded', function () { initAll(document); initHeaderScroll(); });

  /* Re-init sections edited in the Shopify Theme Editor */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
